import React from 'react';
import '../../index.css';
import { inferVerbTiming, isVerbWord } from '../../utilities/verbTiming';

const timingOrder = [
    ['Past', 'analytics-bar-past'],
    ['Present', 'analytics-bar-present'],
    ['Future', 'analytics-bar-future'],
];

const getTimingCounts = (details = []) => {
    const counts = { Past: 0, Present: 0, Future: 0 };
    let total = 0;

    details.forEach((word) => {
        if (!isVerbWord(word)) return;
        const timing = inferVerbTiming(word);
        if (!timing) return;
        total += 1;
        if (Object.prototype.hasOwnProperty.call(counts, timing)) {
            counts[timing] += 1;
        }
    });

    return { counts, total };
};

const formatScore = (value) => {
    const score = Number(value);
    if (!Number.isFinite(score)) return null;
    return Math.round(score * 100);
};

const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

const getActionWords = (details = []) => uniqueValues(details
    .filter((word) => isVerbWord(word))
    .map((word) => `${word.text || ''}`.trim().toLowerCase()))
    .slice(0, 5);

function AnalyticsPanel({ data }) {
    const sentence = data?.sentence || {};
    const { counts, total } = getTimingCounts(sentence.details || []);
    const sentimentScore = formatScore(sentence.sentiment?.[1]);
    const sentimentPercent = sentimentScore === null ? null : Math.round((sentimentScore + 100) / 2);
    const sentimentLabel = sentimentScore === null
        ? ''
        : sentimentScore >= 14 ? 'Positive' : sentimentScore <= -14 ? 'Negative' : 'Neutral';
    const baseContext = [data?.what?.testament, data?.what?.grouping, sentence.heading]
        .filter(Boolean);
    const peopleCount = (data?.who?.known?.length || 0) + (data?.who?.unknown?.length || 0);
    const placesCount = data?.where?.known?.length || 0;
    const actionWords = getActionWords(sentence.details || []);
    const suppliedAnalysis = data?.analytics || data?.analysis || {};
    const suppliedThemes = Array.isArray(suppliedAnalysis.themes)
        ? suppliedAnalysis.themes.map((theme) => theme?.label || theme?.name || theme).filter(Boolean)
        : [];
    const themes = uniqueValues([...suppliedThemes]).slice(0, 5);
    const context = uniqueValues([...baseContext, ...themes]).slice(0, 7);
    const narrativeVoice = suppliedAnalysis.voice || sentence.voice || sentence.speaker;
    const audience = suppliedAnalysis.audience || sentence.audience;
    const timeline = data?.when || {};
    const peopleBorn = Array.isArray(timeline.PeopleBorn) ? timeline.PeopleBorn : [];
    const booksWritten = Array.isArray(timeline.BooksWritten) ? timeline.BooksWritten : [];
    const hasStorySetting = Boolean(timeline.Concerns || peopleBorn.length || booksWritten.length);

    return (
        <div className="analytics-panel">
            <div className="analytics-panel-intro">
                <span className="analytics-panel-eyebrow">Reading analysis</span>
                <h3>Key signals</h3>
                <p>Simple indicators drawn from the selected passage.</p>
            </div>

            <section className="analytics-panel-card analytics-profile-card">
                <span className="analytics-panel-label">Passage profile</span>
                <div className="analytics-profile-grid">
                    <div><strong>{peopleCount}</strong><span>People</span></div>
                    <div><strong>{placesCount}</strong><span>Places</span></div>
                    <div><strong>{data?.what?.grouping || data?.what?.testament || '—'}</strong><span>Shape</span></div>
                </div>
            </section>

            {total > 0 ? (
                <section className="analytics-panel-card">
                    <div className="analytics-panel-card-heading">
                        <span className="analytics-panel-label">Verb timing</span>
                        <span className="analytics-panel-value">{total} verbs</span>
                    </div>
                    <div className="analytics-timing-list">
                        {timingOrder.map(([label, className]) => {
                            const count = counts[label];
                            const percentage = Math.round((count / total) * 100);
                            return (
                                <div className="analytics-timing-row" key={label}>
                                    <span>{label}</span>
                                    <div className="analytics-timing-track">
                                        <span className={className} style={{ width: `${percentage}%` }} />
                                    </div>
                                    <strong>{percentage}%</strong>
                                </div>
                            );
                        })}
                    </div>
                </section>
            ) : null}

            {sentimentPercent !== null ? (
                <section className="analytics-panel-card analytics-tone-card">
                    <div className="analytics-panel-card-heading">
                        <span className="analytics-panel-label">Tone</span>
                        <strong className="analytics-panel-value">{sentimentLabel}</strong>
                    </div>
                    <div className="analytics-tone-track">
                        <span style={{ width: `${sentimentPercent}%` }} />
                    </div>
                    <div className="analytics-tone-scale">
                        <span>Negative</span>
                        <span>{sentimentScore > 0 ? '+' : ''}{sentimentScore}</span>
                        <span>Positive</span>
                    </div>
                </section>
            ) : null}

            {context.length > 0 || actionWords.length > 0 ? (
                <section className="analytics-panel-card">
                    <span className="analytics-panel-label">Context</span>
                    {context.length > 0 ? (
                        <div className="analytics-context-list">
                            {context.map((item) => <span key={item}>{item}</span>)}
                        </div>
                    ) : null}
                    {actionWords.length > 0 ? (
                        <div className="analytics-action-list">
                            <strong>Key actions</strong>
                            <span>{actionWords.join(' · ')}</span>
                        </div>
                    ) : null}
                </section>
            ) : null}

            {narrativeVoice || audience ? (
                <section className="analytics-panel-card">
                    <span className="analytics-panel-label">Narrative frame</span>
                    {narrativeVoice ? <div className="analytics-story-row"><strong>Voice</strong><span>{narrativeVoice}</span></div> : null}
                    {audience ? <div className="analytics-story-row"><strong>Audience</strong><span>{audience}</span></div> : null}
                </section>
            ) : null}

            {hasStorySetting ? (
                <section className="analytics-panel-card analytics-story-card">
                    <span className="analytics-panel-label">Story setting</span>
                    {timeline.Concerns ? <p>{timeline.Concerns}</p> : null}
                    {peopleBorn.length > 0 ? (
                        <div className="analytics-story-row">
                            <strong>People born</strong>
                            <span>{peopleBorn.map((person) => person.name).filter(Boolean).join(', ')}</span>
                        </div>
                    ) : null}
                    {booksWritten.length > 0 ? (
                        <div className="analytics-story-row">
                            <strong>Books written</strong>
                            <span>{booksWritten.map((book) => book.description).filter(Boolean).join(', ')}</span>
                        </div>
                    ) : null}
                </section>
            ) : null}

            <div className="analytics-source-note">
                <strong>How this is formed</strong>
                <span>Signals are derived from passage metadata and linguistic analysis.</span>
                <span>Source details are listed in Footnotes, Sources and Credits.</span>
            </div>
        </div>
    );
}

export default AnalyticsPanel;
